import React, { useCallback, useEffect } from 'react';

import { getCloudId } from '@helpcenter/meta';
import { ScreenName } from '@helpcenter/constants';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Lozenge from '@atlaskit/lozenge/lozenge';
import ButtonItem from '@atlaskit/menu/button-item';
import { MODAL_CONFIG } from '@atlassian/edition-benefits-modal/constants';
import { editionBenefitsModalEntryPoint } from '@atlassian/edition-benefits-modal/edition-benefits-modal-entrypoint';
import type { TouchpointId } from '@atlassian/edition-benefits-modal/types';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { ModalTrigger } from '@atlassian/entry-points/modal-trigger';

import hubMessages from '../messages';

/**
 * Has the item report its exposure when the Customise menu opens.
 */
const HUBS_CUSTOMIZE_MENU_ANALYTICS_GATE = 'hubs_customize_menu_analytics';
const isHubCustomizeMenuAnalyticsEnabled = (): boolean =>
	FeatureGates.checkGate(HUBS_CUSTOMIZE_MENU_ANALYTICS_GATE);

interface PremiumUpsellMenuItemProps {
	label: string;
	testId: string;
	touchpointId: TouchpointId;
	/** Called on press, after the trigger click is reported. */
	onClick?: () => void;
}

/**
 * A Customise menu item that a Premium-gated feature is behind. Instead of
 * navigating, it opens the shared edition benefits modal, so an un-entitled admin
 * sees what upgrading unlocks rather than a screen they cannot use.
 *
 * `variantId` is the same `help-center-customization` every other help center
 * upsell uses — the modal is variant-driven, so a new surface contributes a
 * touchpoint for attribution and never a new variant.
 */
export const PremiumUpsellMenuItem = ({
	label,
	testId,
	touchpointId,
	onClick,
}: PremiumUpsellMenuItemProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// `ModalTrigger` does not emit the trigger analytics the legacy trigger did, so
	// the surface reports its own view and click for parity with the other upsell
	// entry points, which is what lets the upsell funnel count this exposure.
	const fireTriggerEvent = useCallback(
		(action: 'viewed' | 'clicked') => {
			createAnalyticsEvent({
				action,
				eventType: 'ui',
				actionSubject: 'modalTrigger',
				actionSubjectId: 'editionBenefitsModalTrigger',
				attributes: {
					touchpointId,
					product: 'jira-service-management',
					variantId: 'help-center-customization',
					source: 'hubCustomizeMenu',
				},
			}).fire('growth');
		},
		[createAnalyticsEvent, touchpointId],
	);

	// The item only mounts while the menu is open, so mounting is the view.
	useEffect(() => {
		if (isHubCustomizeMenuAnalyticsEnabled()) {
			fireTriggerEvent('viewed');
		}
	}, [fireTriggerEvent]);

	const fireTriggerClicked = useCallback(() => {
		fireTriggerEvent('clicked');
		onClick?.();
	}, [fireTriggerEvent, onClick]);

	return (
		<ErrorBoundary
			packageName={ScreenName.HELP_CENTER}
			id={`hub-customize-menu-${touchpointId}`}
			// A broken upsell must not take the rest of the menu with it.
			renderError={() => null}
		>
			{/*
			 * Hub config panels and popups sit outside any ModalContextProvider, and
			 * `ModalTrigger` reads a context whose default `openModal` throws rather
			 * than no-oping, so this brings its own. The trigger binds the press
			 * through the ref it hands back, so the item needs no onClick of its own.
			 */}
			<ModalContextProvider>
				<ModalTrigger
					entryPoint={editionBenefitsModalEntryPoint}
					entryPointParams={{ cloudId: getCloudId(), product: 'jira-service-management' }}
					entryPointProps={{ touchpointId, variantId: 'help-center-customization' }}
					modalProps={MODAL_CONFIG}
				>
					{({ ref }) => (
						<ButtonItem
							ref={ref as React.Ref<HTMLElement>}
							testId={testId}
							onClick={fireTriggerClicked}
							iconAfter={
								<Lozenge appearance="discovery">
									{formatMessage(hubMessages.premiumLozenge)}
								</Lozenge>
							}
						>
							{label}
						</ButtonItem>
					)}
				</ModalTrigger>
			</ModalContextProvider>
		</ErrorBoundary>
	);
};
